import React, { useState, useEffect, useMemo } from "react"
import Flink from '~/components/shared/flink'
import { useTheme } from '~/contexts/ThemeContext'
import useTestFunnelHook from '~/utils/test-funnel'
import useAnalytics from '~/utils/analytics'
import useInventoryHook from '~/utils/use-inventory'
import { jnstyls, oneSize, imgSize, altText } from '~/utils/helpers'

import TileImages from '~/components/tile/tile-images'
import SwatchGroup from '~/components/shared/swatches'
import Callouts from '~/components/shared/callouts'
import FavProd from '~/components/shared/fav-prod'

import { aria_btn, qsizes } from "~/styles/app.module.scss"
import * as styles from "~/styles/tiles/tile.module.scss"

const TileProduct = ({ product, tile_ind, is_plp, flats, isInView, handleQuick }) => {
  const { isMobile, setPdpVariant, isSHM, customer } = useTheme()
  const { fnnl_tile } = useTestFunnelHook()
  const { quickinv } = useInventoryHook()
  const { elev_select, ga_tile_click, ga_tile_interact } = useAnalytics()
  
  const { title, handle, media, pl_name, first_product_price, first_full_price, percent_off, 
    color, pl_colors, variants, callouts, alt_cuts } = product
  

  const firstImg = useMemo(() => {
    if (!Array.isArray(media)) return null
    return media.find(x => x.type === 'IMAGE')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const is_gift = product.pl_pima === 99999
  
  const standard = !is_gift && pl_colors?.hasOwnProperty('standard') ? pl_colors.standard : []
  let colors = !is_gift ? standard.concat(pl_colors?.venice_wash || []) : []
  let color_ind = colors.findIndex(x => x.handle === product.handle);

  if (color_ind !== -1) {
    let item = colors.splice(color_ind, 1)[0]
    colors.unshift(item)
  }

  const meta_img = media.find(x => x.flat) || media.find(x => x.face) || media.find(x => x.type === 'IMAGE')
  const [tileInv, setTileInv] = useState(false)
  const [activeColor, setActiveColor] = useState(color?.name || null)

  const handleSelect = () => {
    ga_tile_click(product, tile_ind)
    elev_select(product, tile_ind)
    fnnl_tile()
  }

  const sortSizes = (sizes) => {
    const size_order = ['xxs','2s','xs','s','m','l','xl','xxl','2x','xxxl','3x']
    let firstsize = sizes[0].title.toString().toLowerCase()
    if (size_order.includes(firstsize)) {
      return sizes.sort((a, b) => size_order.indexOf(a.title.toLowerCase()) - size_order.indexOf(b.title.toLowerCase()) )
    }

    return sizes.sort((a, b) => a.title - b.title)
  }

  const handleInteract = (action) => {
    ga_tile_interact(product, action)
    if (window.location.pathname.includes('womens-tees')) {
      window._conv_q = window._conv_q || []
      window._conv_q.push(["triggerConversion", "100449410"])
    }

    if (action === 'tile click: swatch' || action === 'tile click: more colors') {
      ga_tile_interact(product, 'tile click: quick pdp')
      fnnl_tile()
    }
  }

  const handleSize = (variant) => {
    setPdpVariant(variant)
    handleQuick(handle)

    if (window.location.pathname.includes('womens-tees')) {
      window._conv_q = window._conv_q || []
      window._conv_q.push(["triggerConversion", "100449410"])
    }
  }

  const colorClick = (handle) => {
    handleQuick(handle)

  }

  const deskhoverQuick = (handle) => {
    handleQuick(handle)

    if (window.location.pathname.includes('womens-tees')) {
      window._conv_q = window._conv_q || []
      window._conv_q.push(["triggerConversion", "100449410"])
    }
  }

  const mobBtnQuick = (handle) => {
    handleQuick(handle)

    if (window.location.pathname.includes('womens-tees')) {
      window._conv_q = window._conv_q || []
      window._conv_q.push(["triggerConversion", "100449410"])
    }
  }

  const handleInv = async () => {
    if (isMobile || !!tileInv) return
    const inv = await quickinv(product.pl_pima, product.shop_id)
    setTileInv(inv)
  }

  const checkAvail = (variant) => {
    if (!tileInv) return variant.available

    const fulfill = tileInv.fulfill
    const vfulfill = fulfill[variant.title]
    const vjson = tileInv?.variants?.find(x => x.id === variant.id)
    if (!vjson) return variant.available

    const var_wh = (vjson.inv.DW || vjson.inv['BL - Ecommerce'])
    const ship_loc_avail = !isSHM ? !!vfulfill?.ship : var_wh

    let ship_avail = vjson.available
    let pick_avail = vjson.available && !!vfulfill?.pickup
    if (vfulfill) {
      ship_avail = vjson.available && ship_loc_avail
    }

    if (!ship_avail && !pick_avail) return styles.disabled
    if (ship_avail && pick_avail) return ''
    return (pick_avail && !ship_avail) ? styles.pdot : ''
  }
  
  const colorInteraction = (name) => {
    setActiveColor(name ? name : color.name);
  }

  useEffect(() => {
    if (isInView && media?.length > 0) {
      media.filter(x => x.type === 'IMAGE').forEach(x => {
        const preimg = new Image()
        preimg.src = imgSize(x.src,700)
      })
    }
  }, [isInView, product])


  return (
    <>
      {is_plp && (
        <>
          <meta itemProp="name" content={title} />
          <meta itemProp="url" content={`https://www.buckmason.com/products/${handle}`} />
          {meta_img && (
            <meta itemProp="image" content={imgSize(meta_img.src,1100)} />
          )}
        </>
      )}

      <div className={styles.images} onMouseEnter={(e)=> handleInv()}>
        {(isInView && media?.length > 0 ) && (
          <TileImages 
            media={media} 
            handle={handle} 
            title={title} 
            wr_class={'tile_link'} 
            linked={true} 
            tile_ind={tile_ind} 
            toggleFlats={flats} 
            onHandleSelect={handleSelect}
            gaInteract={handleInteract}
            gender={product.gender}
            ids={{collId: is_plp, plId: product.product_line.mid, prodId: product.mid}}
          />
        )}
        {(!isInView && firstImg) && (
          <Flink
            href={`/products/${handle}`}
            aria-label={title}
            onClick={handleSelect}
          >
            <img 
              src={imgSize(firstImg.src,700)}
              data-src={imgSize(firstImg.src,700)}
              alt={altText(firstImg.alt, title)}
              className={jnstyls([styles.img_placehold])} 
              width="400" 
              height="532" 
              loading={tile_ind < 6 ? undefined : "lazy"}
              decode="async"
            />
          </Flink>
        )}

        {alt_cuts && (
          <div className={styles.alts} tile-alts="">
            {alt_cuts.opts.length} {alt_cuts.type}s
          </div>
        )}

        {(is_plp && variants?.length > 0 && !is_gift) && (
          <div className={jnstyls([qsizes, styles.qsizes_desk])} tile-sizes="">
            {sortSizes(variants).map( variant => 
              <button 
                type="button"
                key={variant.id}
                className={jnstyls([aria_btn, checkAvail(variant)])} 
                aria-label={`Add size ${variant.title} to cart`} 
                onClick={(e)=> handleSize(variant)}
              >
                {oneSize(variant.title)}
              </button>
            )}
            <button 
              type="button"
              className={styles.qview} 
              qview-d=""
              onClick={(e)=> deskhoverQuick(handle)}
            >
              QUICK VIEW
            </button>
          </div>
        )}
      </div>

      <div className={styles.infos} tile-infos="">
        <div className={styles.info}>
          <Flink 
            href={`/products/${handle}`} 
            className={jnstyls([styles.tile_link, styles.tile_title])} 
            tile-title=""
            onClick={(e)=> handleSelect()}
          >
            {pl_name ? pl_name : title}
          </Flink>
          <div data-product-price="" className={styles.price} tile-price="">
            {percent_off ? (
              <>
                <span className={styles.ptPricePercent}>
                  ({percent_off}% OFF)
                </span>
                <span className={styles.ptPriceLow}>
                  ${first_product_price}
                </span>
                <span className={styles.ptPriceFull}>
                  ${first_full_price}
                </span>
              </>
            ) : (
              <span>${first_product_price}</span>
            )}
          </div>
        </div>

        {(customer && localStorage.getItem('ward')) && (
          <FavProd prod_id={product.mid} tile={true} />
        )}

        {/*{!is_gift && color && (
          <div className={jnstyls([styles.info, styles.color])}>
            {activeColor}
          </div>
        )}*/}

        {!is_gift && !handle.includes('candle') && colors.length > 0 && (
          <div className={styles.info} tile-color="">
            <SwatchGroup 
              prod_handle={handle} 
              color_group={colors} 
              trim={8} 
              onInteraction={colorInteraction} 
              gaInteract={handleInteract}
              colorClick={colorClick}
            />
          </div>
        )}

        <div className={styles.info} tile-colorqty="">
          {colors.length} Color{colors.length > 1 ? 's' : ''}
        </div>

        {callouts?.length > 0 && (
          <div className={styles.info} tile-calls="" style={{fontSize: 'inherit'}}> 
            <Callouts callout_arr={callouts} wr_class={`${styles.info} ${styles.callouts}`} gaInteract={handleInteract} />
          </div>
        )}

        <button 
          type="button"
          className={styles.qview_mob} 
          qview-d=""
          onClick={(e)=> {mobBtnQuick(handle); } }
        >
          QUICK VIEW
        </button>

      </div>
    </>
  )
}

export default TileProduct
